import { supabase } from '@/lib/supabase';

export type CommercialAgreement = {
  ok: boolean;
  /** Present when ok=false (e.g. not_found). */
  fail_reason?: string;
  subscription_id?: string;
  organization_id?: string;
  plan_code?: string;
  billing_cycle?: 'monthly' | 'yearly' | 'none' | string;
  status?: string;
  list_price_agorot?: number | null;
  catalog_list_price_agorot?: number | null;
  agreed_price_agorot?: number | null;
  discount_type?: 'percent' | 'fixed' | null;
  discount_value?: number | null;
  discount_ends_at?: string | null;
  billing_started_at?: string | null;
  first_month_free?: boolean;
  /** Commercial override reason from the agreement. */
  reason?: string | null;
  price_basis?: 'net' | 'gross';
  history?: Array<{
    id: string;
    created_at: string;
    admin_email: string | null;
    action: string;
    metadata: Record<string, unknown>;
  }>;
};

export type SetCommercialAgreementInput = {
  planCode: string;
  billingCycle: 'monthly' | 'yearly';
  /** Net (+VAT) agorot — never VAT-inclusive. */
  listPriceAgorot: number;
  /** Net (+VAT) agorot — never VAT-inclusive. Null clears agreed override. */
  agreedPriceAgorot: number | null;
  discountType: 'percent' | 'fixed' | null;
  discountValue: number | null;
  discountEndsAt: string | null;
  billingStartedAt: string | null;
  firstMonthFree: boolean;
  reason: string | null;
};

function nisToAgorot(nis: string): number | null {
  const trimmed = nis.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) throw new Error('Invalid NIS amount');
  return Math.round(n * 100);
}

function agorotToNisInput(agorot: number | null | undefined): string {
  if (agorot == null) return '';
  return (Number(agorot) / 100).toFixed(2);
}

export const commercialAgreementApi = {
  get: async (orgId: string): Promise<CommercialAgreement> => {
    const { data, error } = await supabase.rpc('admin_get_org_commercial_agreement', {
      p_org_id: orgId,
    });
    if (error) throw error;
    return (data ?? { ok: false }) as CommercialAgreement;
  },

  set: async (orgId: string, input: SetCommercialAgreementInput): Promise<CommercialAgreement> => {
    const { data, error } = await supabase.rpc('admin_set_org_commercial_agreement', {
      p_org_id: orgId,
      p_plan_code: input.planCode,
      p_billing_cycle: input.billingCycle,
      p_list_price_agorot: input.listPriceAgorot,
      p_agreed_price_agorot: input.agreedPriceAgorot,
      p_discount_type: input.discountType,
      p_discount_value: input.discountValue,
      p_discount_ends_at: input.discountEndsAt,
      p_billing_started_at: input.billingStartedAt,
      p_first_month_free: input.firstMonthFree,
      p_reason: input.reason,
    });
    if (error) throw error;
    return (data ?? { ok: false }) as CommercialAgreement;
  },

  nisToAgorot,
  agorotToNisInput,
};
